
import React from 'react';
import SuccessStoryBentoCard from '@/components/ui/SuccessStoryBentoCard';
import { SuccessStoryData } from '@/data/successStories';
import { Grid3X3, LayoutGrid } from 'lucide-react';

interface SuccessStoryGridProps {
  stories: SuccessStoryData[];
}

const SuccessStoryGrid: React.FC<SuccessStoryGridProps> = ({ stories }) => {
  const [gridType, setGridType] = React.useState<'bento' | 'uniform'>('bento');

  if (stories.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No success stories found matching your search.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Layout options */}
      <div className="flex justify-end mb-4">
        <div className="bg-white/90 shadow-sm border border-purple-100 rounded-lg p-1 inline-flex space-x-1">
          <button 
            className={`p-2 rounded ${gridType === 'bento' ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:text-purple-600'}`}
            onClick={() => setGridType('bento')}
            aria-label="Bento grid layout"
            title="Bento grid layout"
          >
            <LayoutGrid size={18} />
          </button>
          <button 
            className={`p-2 rounded ${gridType === 'uniform' ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:text-purple-600'}`}
            onClick={() => setGridType('uniform')}
            aria-label="Uniform grid layout"
            title="Uniform grid layout"
          >
            <Grid3X3 size={18} />
          </button>
        </div>
      </div>
      
      {/* Bento Grid or Uniform Grid */}
      <div className={`grid gap-4 sm:gap-5 ${
        gridType === 'uniform' 
          ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
          : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-6 lg:grid-cols-12'
      } auto-rows-fr`}>
        {stories.map((story, index) => {
          // Create an asymmetric grid layout similar to the provided image
          let sizeClass = '';
          let sizeType: 'small' | 'medium' | 'large' = 'medium'; // Default to medium for better content display
          
          if (gridType === 'bento') {
            // Bento layout - asymmetric
            if (index === 0) {
              // First story is large and spans the full width on mobile, 2/3 on tablet+
              sizeClass = 'sm:col-span-2 md:col-span-6 lg:col-span-8 sm:row-span-2';
              sizeType = 'large';
            } 
            else if (index === 1) {
              // Second story is medium
              sizeClass = 'sm:col-span-2 md:col-span-6 lg:col-span-4 sm:row-span-1';
              sizeType = 'medium';
            }
            // Different patterns for other stories
            else if (index % 7 === 2) {
              sizeClass = 'sm:col-span-1 md:col-span-3 lg:col-span-4 sm:row-span-1';
              sizeType = 'small';
            }
            else if (index % 7 === 3) {
              sizeClass = 'sm:col-span-1 md:col-span-3 lg:col-span-4 sm:row-span-1';
              sizeType = 'small';
            }
            else if (index % 7 === 4) {
              sizeClass = 'sm:col-span-2 md:col-span-6 lg:col-span-4 sm:row-span-1';
              sizeType = 'medium';
            }
            else if (index % 7 === 5) {
              sizeClass = 'sm:col-span-1 md:col-span-3 lg:col-span-6 sm:row-span-1';
              sizeType = 'medium';
            }
            else if (index % 7 === 6) {
              sizeClass = 'sm:col-span-1 md:col-span-3 lg:col-span-6 sm:row-span-1';
              sizeType = 'small';
            }
            else {
              sizeClass = 'sm:col-span-1 md:col-span-3 lg:col-span-4 sm:row-span-1';
              sizeType = 'small';
            }
          } else {
            // Uniform grid layout
            sizeClass = '';
            sizeType = 'medium';
          }
          
          // Create animation delay classes for staggered entry
          const animationDelay = `bento-delay-${index % 5 + 1}`;
          
          return (
            <div 
              key={story.id} 
              className={`${sizeClass} transform transition-all duration-300 h-full min-h-[280px] bento-entry-animation ${animationDelay}`}
            >
              <SuccessStoryBentoCard
                id={story.id}
                name={story.name}
                title={story.title}
                oneLiner={story.oneLiner}
                excerpt={story.excerpt}
                image={story.image}
                stats={story.stats}
                size={sizeType}
                fullStory={story.fullStory}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SuccessStoryGrid;
